import { useId, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { mfaFresh, useAuth } from '../lib/auth';
import { ErrorBox, Loading, Section, useApi } from './kit';
import { toast } from './toasts';

// Closing a workspace (src/workspace/close.ts). The owner's danger zone in Settings and the ops org page show the same
// list, counted by the server: what stops, what is deleted for good, what is cancelled, what is revoked, what is kept.

export type ClosePreview = {
  org: { id: string; name: string; status: string; closed_at: string | null; closed_by: string | null; close_reason: string | null };
  blocked: string | null;
  agency_clients: Array<{ id: string; name: string }>;
  campaigns: { active: number; other: number };
  linkedin_accounts: number;
  ads_running: number;
  managed_sending: { domains: string[]; mailboxes: string[] };
  subscription: { plan: string; status: string; cancels: boolean; paid_by: string } | null;
  agency: { name: string; billed_to: string } | null;
  credits_available: number;
  api_keys: number;
  webhooks: number;
  connections: { crm: number; ads: number; smartlead: number };
  members: number;
  sandbox: boolean;
  mfa_fresh?: boolean;
};

const n = (k: number, one: string, many = `${one}s`) => `${k} ${k === 1 ? one : many}`;

/** The list itself: one line per consequence, only the ones that apply. */
export function CloseConsequences({ p, who = 'you' }: { p: ClosePreview; who?: 'you' | 'staff' }) {
  const conns = [p.connections.crm ? n(p.connections.crm, 'CRM') : null, p.connections.ads ? n(p.connections.ads, 'ad account') : null,
    p.connections.smartlead ? 'its own Smartlead key' : null, p.linkedin_accounts ? n(p.linkedin_accounts, 'LinkedIn account') : null].filter(Boolean);
  const plan = p.subscription?.cancels
    ? p.subscription.paid_by === 'agency'
      ? `The ${p.subscription.plan} plan ${p.agency?.name ?? 'the agency'} pays for ends today; the agency is credited the unused days.`
      : `The ${p.subscription.plan} plan is cancelled today. The rest of the current period is not refunded.`
    : null;
  return (
    <div className="close-consequences">
      <h3>Stops now</h3>
      <ul>
        <li>Nothing is sent from this workspace any more: a kill switch stops every outside action.</li>
        {p.campaigns.active ? <li>{n(p.campaigns.active, 'sending campaign')} {p.campaigns.active === 1 ? 'is' : 'are'} paused, in Smartlead too.</li> : null}
        {p.campaigns.other ? <li>{n(p.campaigns.other, 'other campaign')} (drafts and paused) can never start.</li> : null}
        {p.linkedin_accounts ? <li>LinkedIn auto-sending stops on {n(p.linkedin_accounts, 'account')}.</li> : null}
        {p.ads_running ? <li>{n(p.ads_running, 'running ad')} {p.ads_running === 1 ? 'is' : 'are'} paused on its platform.</li> : null}
      </ul>
      {p.managed_sending.domains.length ? <>
        <h3>Deleted for good</h3>
        <p className="warn-text">The sending domains and mailboxes Outlevo set up are deleted and <strong>can't be recovered</strong>: {p.managed_sending.domains.join(', ')}
          {p.managed_sending.mailboxes.length ? ` (${n(p.managed_sending.mailboxes.length, 'mailbox', 'mailboxes')}: ${p.managed_sending.mailboxes.join(', ')})` : ''}. {who === 'you' ? 'Your' : 'The workspace\'s'} own mailboxes and own Smartlead are not touched.</p>
      </> : null}
      {plan || p.credits_available > 0 ? <>
        <h3>Billing</h3>
        <ul>
          {plan ? <li>{plan}</li> : null}
          {p.agency ? <li>{p.agency.name} no longer manages this workspace; its people lose access and its owners are told.</li> : null}
          {p.credits_available > 0 ? <li>{n(p.credits_available, 'unused credit')} can no longer be used.</li> : null}
        </ul>
      </> : null}
      <h3>Revoked</h3>
      <ul>
        <li>{n(p.members, 'member')} lose{p.members === 1 ? 's' : ''} access{p.sandbox ? '; test mode closes too' : ''}.</li>
        {p.api_keys ? <li>{n(p.api_keys, 'API key')} stop{p.api_keys === 1 ? 's' : ''} working.</li> : null}
        {p.webhooks ? <li>{n(p.webhooks, 'webhook')} stop{p.webhooks === 1 ? 's' : ''} delivering.</li> : null}
        {conns.length ? <li>Disconnected: {conns.join(', ')}.</li> : null}
      </ul>
      <h3>Kept</h3>
      <p>The data stays in Outlevo: accounts, contacts, campaigns, reports and the audit trail are not deleted. To get a copy or have it deleted,
        write to <a href="mailto:support@notify.outlevo.com">support@notify.outlevo.com</a>; that is a data request, handled separately{who === 'you' ? <>. Export lists you want first (<Link to="/accounts">Accounts</Link> has Export buttons)</> : null}.</p>
    </div>
  );
}

/** Settings → Close workspace: owner only, a fresh second factor, the name typed back. */
export function CloseWorkspace() {
  const { me, mfaAgeMs, supabase, refresh } = useAuth();
  const nav = useNavigate();
  const q = useApi<ClosePreview>('/portal/workspace/close/preview');
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const nameId = useId(); const reasonId = useId();
  if (me?.org.sandbox_of) return <Section title="Close workspace"><p>This is test mode. It closes together with the live workspace: turn test mode off to close it.</p></Section>;
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  const p = q.data;
  const fresh = mfaFresh(mfaAgeMs);
  const matches = typed.trim() === p.org.name.trim();
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try {
      await api('/portal/workspace/close', { method: 'POST', body: { confirm_name: typed, ...(reason.trim() ? { reason: reason.trim() } : {}) } });
      toast(`${p.org.name} was closed. Its data is kept; write to support@notify.outlevo.com for a copy or to have it deleted.`);
      // the auth hook moves the session to another workspace (or none: then the app offers to create one)
      await supabase?.auth.refreshSession();
      await refresh();
      nav('/');
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };
  return (
    <Section title="Close workspace">
      <div className="danger-zone">
        <p><strong>Closing {p.org.name} cannot be undone.</strong> This is exactly what happens:</p>
        <CloseConsequences p={p} />
        {p.blocked ? <p className="error" role="alert">{p.blocked}{p.agency_clients.length ? `: ${p.agency_clients.map((c) => c.name).join(', ')}` : ''}.</p> : (
          <form onSubmit={submit} className="form">
            {!fresh ? <p className="banner" role="status">Closing needs a second factor in the last 12 hours. <Link to="/mfa">Verify now</Link></p> : null}
            <div className="field"><label htmlFor={nameId}>Type <b>{p.org.name}</b> to confirm</label>
              <input id={nameId} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} /></div>
            <div className="field"><label htmlFor={reasonId}>Why are you closing it? (optional)</label>
              <textarea id={reasonId} rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} /></div>
            {err ? <ErrorBox error={err} doing="close the workspace" /> : null}
            <button type="submit" className="btn btn-danger" disabled={busy || !matches || !fresh}>{busy ? 'Closing…' : 'Close workspace'}</button>
          </form>
        )}
      </div>
    </Section>
  );
}

/** The ops org page: staff with the compliance permission close a workspace with a reason (audited as them). */
export function OpsCloseWorkspace({ orgId, onDone }: { orgId: string; onDone: () => void }) {
  const q = useApi<ClosePreview>(`/ops/orgs/${orgId}/close/preview`);
  const [reason, setReason] = useState('');
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const reasonId = useId(); const sureId = useId();
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  const p = q.data;
  if (p.org.status === 'closed') {
    return <Section title="Closed"><p>Closed {p.org.closed_at ? new Date(p.org.closed_at).toLocaleString() : ''} by {p.org.closed_by ?? 'unknown'}: {p.org.close_reason ?? 'no reason recorded'}.</p></Section>;
  }
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try {
      await api(`/ops/orgs/${orgId}/close`, { method: 'POST', body: { reason: reason.trim() } });
      toast(`${p.org.name} was closed`);
      onDone();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };
  return (
    <Section title="Close workspace">
      <div className="danger-zone">
        <CloseConsequences p={p} who="staff" />
        {p.blocked ? <p className="error" role="alert">{p.blocked}{p.agency_clients.length ? `: ${p.agency_clients.map((c) => c.name).join(', ')}` : ''}.</p> : (
          <form onSubmit={submit} className="form">
            <div className="field"><label htmlFor={reasonId}>Reason (kept on the workspace and in its audit trail)</label>
              <textarea id={reasonId} rows={2} minLength={3} maxLength={500} required value={reason} onChange={(e) => setReason(e.target.value)} /></div>
            <div className="field field-check"><input id={sureId} type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)} />
              <label htmlFor={sureId}>Close {p.org.name}: this cannot be undone</label></div>
            {err ? <ErrorBox error={err} doing="close the workspace" /> : null}
            <button type="submit" className="btn btn-danger" disabled={busy || !sure || reason.trim().length < 3}>{busy ? 'Closing…' : 'Close workspace'}</button>
          </form>
        )}
      </div>
    </Section>
  );
}
